import { mdiDelete, mdiDownload, mdiUpload } from "@mdi/js";
import { Button, Modal, Space } from "antd";
import { DocumentMetadataVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import IconButton from "components/ui/IconButton";
import { mapRepairVmToUpdateRepairmanCommand } from "features/repairmen/models";
import useDumbTable from "hooks/useDumbTable";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ExtendedColumnType } from "widgets/table/table-settings/ExtendedColumnType";
import { ColumnConfigParams } from "widgets/table/useTableUtils";
import AddNewDocumentModal from "./AddNewDocumentModal";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";

const { confirm } = Modal;

interface DocumentsTabProps {
  afterDocumentUpload: () => void;
  afterDocumentDelete: () => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  afterDocumentUpload,
  afterDocumentDelete,
}) => {
  const { t } = useTranslation();
  const { repairman } = useSelector((state) => state.repairmenView);
  const [addNewDocumentModalOpen, setAddNewDocumentModalOpen] = useState(false);
  const dispatch = useDispatch();

  const columnParams = useMemo<ColumnConfigParams<DocumentMetadataVm>[]>(
    () => [
      {
        title: t("document.title"),
        key: "name",
        sort: false,
      },
      {
        title: t("document.type"),
        key: "contentType",
        sort: false,
      },
      {
        title: t("document.size"),
        key: "sizeInMB",
        sort: false,
      },
    ],
    [t]
  );

  const handleDownloadButtonClick = useCallback(
    (id?: string) => {
      if (typeof id === "string") {
        dispatch.repairmenView.downloadDocument(id);
      }
    },
    [dispatch.repairmenView]
  );

  const handleDeleteButtonClick = useCallback(
    async (id?: string) => {
      if (typeof id === "string" && repairman) {
        confirm({
          okText: t("common.confirmDeleteModal.yes"),
          okType: "danger",
          cancelText: t("common.confirmDeleteModal.no"),
          title: t("common.confirmDeleteModal.title"),
          onOk: async () => {
            await dispatch.repairmenView.deleteDocument({
              repairmanData: mapRepairVmToUpdateRepairmanCommand(repairman),
              documentId: id,
            });

            afterDocumentDelete();
          },
        });
      }
    },
    [afterDocumentDelete, dispatch.repairmenView, repairman, t]
  );

  const handleModalOk = useCallback(() => {
    afterDocumentUpload();

    setAddNewDocumentModalOpen(false);
  }, [afterDocumentUpload]);

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<DocumentMetadataVm>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 90,
      render(item: any, record) {
        return (
          <Space>
            <Permission acceptableClaims={[Claims.Document.Read]}>
              <IconButton
                path={mdiDownload}
                onClick={() => handleDownloadButtonClick(record.id)}
                title={t("common.download")}
              />
            </Permission>

            <Permission acceptableClaims={[Claims.Document.Write]}>
              <IconButton
                path={mdiDelete}
                onClick={() => handleDeleteButtonClick(record.id)}
                title={t("common.delete")}
              />
            </Permission>
          </Space>
        );
      },
      title: t("common.table.actions"),
    }),
    [handleDeleteButtonClick, handleDownloadButtonClick, t]
  );

  const customTableProps = useDumbTable({
    columnParams,
    list: repairman?.documents,
    actionColumnParams,
    headerOptions: (
      <Permission acceptableClaims={[Claims.Document.Write]}>
        <Button
          type="primary"
          htmlType="submit"
          block
          icon={<MaterialIcon path={mdiUpload} />}
          onClick={() => setAddNewDocumentModalOpen(true)}
        >
          {t("document.addNew")}
        </Button>
      </Permission>
    ),
  });

  return (
    <div>
      <CustomTable<DocumentMetadataVm>
        {...customTableProps}
        selectable={false}
        scroll={{ x: 1000 }}
      />

      <AddNewDocumentModal
        visible={addNewDocumentModalOpen}
        onCancel={() => setAddNewDocumentModalOpen(false)}
        onOk={() => handleModalOk()}
      />
    </div>
  );
};

export default DocumentsTab;
